import { AppBar, Box, Chip, IconButton, Tab, Tabs, Toolbar, Tooltip, Typography } from "@mui/material";
import { Circle } from "@mui/icons-material";
import { useConfig } from "../../contexts/ConfigContext";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import UserButton from "../login/UserButton";
import { NAVBAR_HEIGHT } from "../../pages/Map";
import { useSelectionActions } from "../../store/selection";
import { startTransition, useMemo, useState } from "react";
import useAuth from "../../hooks/useAuth";
import CloseIcon from "@mui/icons-material/Close";
import { useSelectedEntity } from "../../hooks/useSelectedEntity";
import { usePopupActions } from "../../store/popupStore";
import { useMapActions } from "../../store/map";

// Query params to preserve when navigating between tabs
// Do not preserve `admin` so admin settings close when switching pages.
const ALLOWED_KEYS = ["config"];

const Navbar = () => {
  const config = useConfig();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const logoInitial = useMemo(() => config.appTitle.trim().charAt(0).toUpperCase() || "C", [config.appTitle]);
  const { hasWritePermissions, canCreate, canUpdate, canAdminister } = useAuth();
  const { entity } = useSelectedEntity();
  const { reset: resetPopup } = usePopupActions();
  const { setCommonMapState } = useMapActions();

  const { clearPrimarySelection, clearSecondaryItems } = useSelectionActions();

  const navigateWithQueryParams = (path: string) => {
    const nextParams = new URLSearchParams();

    ALLOWED_KEYS.forEach((key) => {
      const value = queryParams.get(key);
      if (value) nextParams.set(key, value);
    });

    // Add default endpoint for /map route here instead of sidebar(useEffect) to avoid race condition
    if (path === "/map" && !nextParams.has("endpoint")) {
      nextParams.set("endpoint", config.apiEndpoints[0]?.tableName);
    }

    const query = nextParams.toString();

    navigate(query ? `${path}?${query}` : path);
  };

  // no trailing slashes in route values
  const normalizePath = (path: string): string => {
    return path.length > 1 ? path.replace(/\/$/, "") : path;
  };

  // Collapse nested routes onto their owning tab value so MUI Tabs always has a
  // matching value: form-builder/import -> "Forms", /admin/* -> "Admin Console".
  const resolveTabValue = (path: string): string => {
    const normalized = normalizePath(path);
    if (normalized.startsWith("/forms") || normalized.startsWith("/form-builder") || normalized.startsWith("/form-import")) return "/forms";
    // Admin console routes highlight the "Admin Console" tab. (The "Settings"
    // overlay lives in the profile menu via the ?admin=true query param and has
    // no tab, so it never affects tab highlighting.)
    if (normalized === "/admin" || normalized.startsWith("/admin/")) return "/admin-console";
    return normalized;
  };

  function handleNavigateToHomePage() {
    clearPrimarySelection();
    clearSecondaryItems();
    startTransition(() => {
      navigateWithQueryParams("/");
    });
  }

  const handleClearSelectedEntity = () => {
    setCommonMapState({ isSpatialDatasetLoading: false });
    clearPrimarySelection();
    resetPopup();
  };

  const selectedEntityName = entity?.name ?? "";
  const showCurrentViewingChip = pathname.startsWith("/map") && !!selectedEntityName;

  return (
    <AppBar position='static' sx={{ zIndex: 1300, height: NAVBAR_HEIGHT, paddingX: "2rem" }}>
      <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <IconButton
            edge='start'
            color='inherit'
            aria-label='menu'
            sx={{ marginRight: "12px", borderRadius: "100%", height: "40px", width: "40px", overflow: "hidden" }}
            onClick={() => navigate("/")}
          >
            {logoLoadFailed ? (
              <Box
                sx={{
                  height: "40px",
                  width: "40px",
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.18)",
                  color: "white",
                  fontWeight: 700,
                  fontSize: "16px"
                }}
              >
                {logoInitial}
              </Box>
            ) : (
              <img
                src={config.logoPath}
                alt='Site logo'
                style={{ height: "40px", width: "40px", objectFit: "cover", display: "block" }}
                onError={() => setLogoLoadFailed(true)}
              />
            )}
          </IconButton>

          <Typography variant='h4' component='div' sx={{ fontSize: "18px", fontWeight: 500 }}>
            {config.appTitle}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          {showCurrentViewingChip && (
            <Tooltip title={`Currently viewing: ${selectedEntityName}`} arrow>
              <Chip
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, width: "100%" }}>
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: "primary.main",
                        animation: "pulse 1.5s infinite",
                        "@keyframes pulse": {
                          "0%": {
                            transform: "scale(1)",
                            opacity: 1
                          },
                          "50%": {
                            transform: "scale(1.1)",
                            opacity: 0.5
                          },
                          "100%": {
                            transform: "scale(1)",
                            opacity: 1
                          }
                        }
                      }}
                    />
                    <Typography
                      variant='body1'
                      sx={{
                        color: "primary.main",
                        minWidth: 0,
                        borderRadius: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        marginLeft: 5
                      }}
                    >
                      {selectedEntityName}
                    </Typography>
                  </Box>
                }
                onDelete={handleClearSelectedEntity}
                deleteIcon={<CloseIcon sx={{ color: "common.white !important", fontSize: 18 }} />}
                sx={{
                  width: 280,
                  maxWidth: 280,
                  height: 36,
                  bgcolor: "rgba(255, 255, 255, 0.6)",
                  border: "1px solid rgba(255,255,255,0.22)",
                  color: "common.white",
                  backdropFilter: "blur(2px)",
                  px: 5,
                  "& .MuiChip-label": {
                    width: "100%",
                    px: 10
                  },
                  "& .MuiChip-deleteIcon": {
                    mr: 0.75,
                    ml: 0.25
                  },
                  borderRadius: 4,
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.85)"
                  }
                }}
              />
            </Tooltip>
          )}
          <Tabs value={resolveTabValue(pathname)} indicatorColor='secondary' textColor='inherit'>
            <Tab sx={{ textTransform: "capitalize" }} label='Home' value='/' onClick={handleNavigateToHomePage} />
            <Tab sx={{ textTransform: "capitalize" }} label='Map' value='/map' onClick={() => navigateWithQueryParams("/map")} />
            {(canCreate || canUpdate) && (
              <Tab
                sx={{ textTransform: "capitalize" }}
                label='Editor'
                value='/editor'
                onClick={() => navigateWithQueryParams("/editor?endpoint=media")}
              />
            )}
            {canCreate && (
              <Tab sx={{ textTransform: "capitalize" }} label='Upload' value='/upload' onClick={() => navigateWithQueryParams("/upload")} />
            )}
            <Tab sx={{ textTransform: "capitalize" }} label='Tools' value='/tools' onClick={() => navigateWithQueryParams("/tools")} />
            {(canCreate || canUpdate) && (
              <Tab sx={{ textTransform: "capitalize" }} label='Forms' value='/forms' onClick={() => navigateWithQueryParams("/forms")} />
            )}
            {canAdminister && (
              <Tab sx={{ textTransform: "capitalize" }} label='Admin Console' value='/admin-console' onClick={() => navigate("/admin")} />
            )}
          </Tabs>
          <UserButton />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
